import { useCallback, useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import {
  adminLogin, adminMe, clearToken, getToken, formatApiError,
  fetchCloseStatus, fetchCloseEntityDetail, fetchClosePortfolio, fetchCloseWhatChanged, fetchClosePeriodView,
} from "@/lib/api";
import { Seo } from "@/components/Seo";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};

const inputCls =
  "w-full bg-transparent border border-[rgba(180,139,63,0.35)] px-4 py-3 text-[var(--bone)] font-serif2 text-lg placeholder:text-[var(--muted)] focus:outline-none focus:border-[var(--gold)] transition-colors";

// deterministic status → visual language (calm, serious; no neon)
const STATUS_META = {
  NEEDS_ATTENTION: { label: "Needs Attention", cls: "border-[#d98b5f] text-[#e7a877]" },
  IN_PROGRESS:     { label: "In Progress",     cls: "border-[rgba(180,139,63,0.5)] text-[var(--gold-lite)]" },
  READY_TO_CLOSE:  { label: "Ready to Close",  cls: "border-[#6fae8f] text-[#8fcbac]" },
  CLOSED:          { label: "Closed",          cls: "border-[rgba(180,139,63,0.3)] text-[var(--muted)]" },
  NOT_STARTED:     { label: "Not Started",     cls: "border-[rgba(180,139,63,0.25)] text-[var(--muted)]" },
  NOT_AVAILABLE:   { label: "No Data",         cls: "border-[rgba(180,139,63,0.25)] text-[var(--muted)]" },
};

function StatusPill({ status, size = "sm" }) {
  const m = STATUS_META[status] || STATUS_META.NOT_AVAILABLE;
  const pad = size === "lg" ? "px-4 py-1.5 text-xs" : "px-2.5 py-1 text-[10px]";
  return (
    <span className={`inline-block font-display uppercase tracking-wide2 border ${pad} ${m.cls}`} data-testid={`status-pill-${status}`}>
      {m.label}
    </span>
  );
}

const Metric = ({ label, value, testid, tone = "bone" }) => (
  <div className="border border-[rgba(180,139,63,0.18)] px-5 py-4" data-testid={testid}>
    <p className="font-display uppercase tracking-wide2 text-[10px] text-[var(--muted)]">{label}</p>
    <p className={`mt-2 font-serif2 text-3xl ${tone === "attention" ? "text-[#e7a877]" : "text-[var(--bone)]"}`}>{value}</p>
  </div>
);

// canonical gate-state visual contract (green satisfied · amber not-started/unknown · red blocking)
const GATE_META = {
  SATISFIED:      { label: "Satisfied",   glyph: "✓", tone: "text-[#8fcbac]",     border: "border-[#6fae8f]",             dot: "#8fcbac" },
  BLOCKED:        { label: "Blocked",     glyph: "✕", tone: "text-[#e88f6a]",     border: "border-[#d9633b]",             dot: "#e88f6a" },
  NEEDS_ATTENTION:{ label: "Needs review",glyph: "!", tone: "text-[#e7a877]",     border: "border-[#d98b5f]",             dot: "#e7a877" },
  NOT_STARTED:    { label: "Not started", glyph: "–", tone: "text-[var(--gold-lite)]", border: "border-[rgba(180,139,63,0.4)]", dot: "#B48B3F" },
  UNKNOWN:        { label: "Unknown",     glyph: "?", tone: "text-[var(--muted)]", border: "border-[rgba(180,139,63,0.25)] border-dashed", dot: "#8f8b7e" },
};

function GateStrip({ gates, entityId }) {
  return (
    <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`gate-strip-${entityId}`} role="list" aria-label="Close gates">
      {(gates || []).map((g) => {
        const m = GATE_META[g.state] || GATE_META.UNKNOWN;
        return (
          <span key={g.gate} role="listitem" title={`${g.gate}: ${m.label}${g.blocks_close ? " (blocks close)" : ""}`}
            aria-label={`${g.gate} ${m.label}`} data-testid={`gate-${g.gate.toLowerCase()}-${entityId}`}
            className={`inline-flex items-center gap-1 border px-2 py-0.5 text-[10px] uppercase tracking-wide2 ${m.tone} ${m.border}`}>
            <span aria-hidden className="font-bold">{m.glyph}</span>
            {g.gate}
          </span>
        );
      })}
    </div>
  );
}

// Compact, accessible gate-STATE legend (text + glyph, never color alone). Derives its state list from the
// live portfolio gates so it can never drift from the canonical gate registry, and does NOT hardcode a gate count.
function GateStateLegend({ gates }) {
  const present = Array.from(new Set((gates || []).map((g) => g.state)));
  const order = ["SATISFIED", "NEEDS_ATTENTION", "BLOCKED", "NOT_STARTED", "UNKNOWN"];
  const states = order.filter((s) => present.length === 0 || present.includes(s) || order.includes(s));
  return (
    <div className="mb-6 border border-[rgba(180,139,63,0.18)] px-4 py-3" data-testid="gate-state-legend" role="list" aria-label="Gate state legend">
      <p className="font-display uppercase tracking-wide2 text-[11px] text-[var(--gold)]/80 mb-2">Gate state legend</p>
      <div className="flex flex-wrap gap-x-5 gap-y-2">
        {states.map((s) => {
          const m = GATE_META[s] || GATE_META.UNKNOWN;
          return (
            <span key={s} role="listitem" data-testid={`legend-${s.toLowerCase()}`}
              className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wide2 ${m.tone}`}>
              <span aria-hidden className="font-bold">{m.glyph}</span>{s} — {m.label}
            </span>
          );
        })}
      </div>
    </div>
  );
}

const coverageText = (g) => (g.total != null && g.total > 0) ? `${g.finalized ?? "—"}/${g.total}` : (g.total === 0 ? "no activity" : "N/A");

function GatesChecklist({ gates }) {
  return (
    <div className="mt-6" data-testid="gates-checklist">
      <p className="font-display uppercase tracking-wide2 text-[11px] text-[var(--gold)]/80 mb-2">What blocks close</p>
      <div className="border border-[rgba(180,139,63,0.18)]" role="list" aria-label="Close gate checklist">
        {(gates || []).map((g) => {
          const m = GATE_META[g.state] || GATE_META.UNKNOWN;
          return (
            <div key={g.gate} role="listitem" data-testid={`checklist-${g.gate.toLowerCase()}`}
              className="flex items-start justify-between gap-3 px-4 py-2.5 border-t border-[rgba(180,139,63,0.1)] first:border-t-0">
              <div className="flex items-center gap-2 min-w-0">
                <span aria-hidden className={`font-bold ${m.tone}`}>{m.glyph}</span>
                <span className="text-sm text-[var(--bone)]">{g.gate}</span>
              </div>
              <div className="text-right">
                <span className={`text-[11px] uppercase tracking-wide2 ${m.tone}`} data-testid={`checklist-state-${g.gate.toLowerCase()}`}>
                  {m.label}{g.blocks_close ? " · blocks" : " · non-blocking"}
                </span>
                <p className="text-[11px] text-[var(--muted)] mt-0.5">{g.reason_code} · coverage {coverageText(g)} · unresolved {g.unresolved ?? "—"}</p>
              </div>
            </div>
          );
        })}
      </div>
      <p className="mt-1.5 text-[10px] uppercase tracking-wide2 text-[var(--muted)]">✓ satisfied · – not-started · ? unknown · ✕ blocking</p>
    </div>
  );
}

function LoginView({ onAuthed }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { const u = await adminLogin(email.trim(), password); onAuthed(u); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail, "Sign-in failed.")); }
    finally { setLoading(false); }
  };
  return (
    <div className="min-h-screen glow-navy flex items-center justify-center px-6">
      <form onSubmit={submit} className="w-full max-w-sm" data-testid="close-login-form">
        <p className="text-center font-display uppercase tracking-luxe text-[10px] text-[var(--gold)]/80">Jennae Michelle Holdings</p>
        <h1 className="mt-3 text-center font-serif2 text-3xl text-[var(--bone)]">Founder Close Console</h1>
        <p className="mt-3 text-center text-sm text-[var(--muted)]">CFO period-close readiness. Authorized access only.</p>
        <div className="mt-10 flex flex-col gap-4">
          <input className={inputCls} type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="close-login-email" />
          <input className={inputCls} type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="close-login-password" />
          <button disabled={loading} className="jmh-cta mt-2 disabled:opacity-50" data-testid="close-login-submit">
            {loading ? "Signing in…" : "Enter"}
          </button>
        </div>
      </form>
    </div>
  );
}

function AttentionSection({ items }) {
  if (!items?.length) {
    return (
      <div className="corner-frame px-6 py-6" data-testid="attention-empty">
        <p className="font-display uppercase tracking-wide2 text-[11px] text-[#8fcbac]">Needs Founder Attention</p>
        <p className="mt-3 text-[var(--muted)]">No reconciliation item currently requires your decision. Routine processing continues in the background under the existing evidence and approval rules.</p>
      </div>
    );
  }
  return (
    <div className="corner-frame px-6 py-6" data-testid="attention-section">
      <p className="font-display uppercase tracking-wide2 text-[11px] text-[#e7a877]">Needs Founder Attention · {items.length}</p>
      <ul className="mt-4 space-y-3">
        {items.map((a, i) => (
          <li key={i} className="border border-[rgba(217,139,95,0.35)] px-4 py-3" data-testid={`attention-item-${i}`}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <span className="text-[var(--bone)] font-serif2 text-lg">{a.entity_name}</span>
              <span className="font-display uppercase tracking-wide2 text-[10px] text-[#e7a877]">{a.reason_code}</span>
            </div>
            <p className="mt-1.5 text-sm text-[var(--muted)] leading-relaxed">{a.why}</p>
          </li>
        ))}
      </ul>
    </div>
  );
}

function EntityDrawer({ entityId, onClose }) {
  const [detail, setDetail] = useState(null);
  const panelRef = useRef(null);
  const closeRef = useRef(null);
  useEffect(() => {
    fetchCloseEntityDetail(entityId).then(setDetail).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [entityId]);
  useEffect(() => {
    const opener = document.activeElement;
    const panel = panelRef.current;
    const onKey = (ev) => {
      if (ev.key === "Escape") { onClose(); return; }
      if (ev.key === "Tab" && panel) {
        const f = panel.querySelectorAll('button, a[href], input, [tabindex]:not([tabindex="-1"])');
        if (!f.length) return;
        const first = f[0], last = f[f.length - 1];
        if (ev.shiftKey && document.activeElement === first) { ev.preventDefault(); last.focus(); }
        else if (!ev.shiftKey && document.activeElement === last) { ev.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener("keydown", onKey);
    if (closeRef.current) closeRef.current.focus();
    return () => {
      document.removeEventListener("keydown", onKey);
      if (opener && typeof opener.focus === "function") opener.focus();  // restore focus to the exact opener
    };
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex" data-testid="entity-drawer">
      <div className="flex-1 bg-black/60" onClick={onClose} aria-hidden="true" />
      <div ref={panelRef} role="dialog" aria-modal="true" aria-labelledby="entity-drawer-title"
        className="w-full max-w-xl h-full overflow-y-auto glow-navy border-l border-[rgba(180,139,63,0.3)] px-6 md:px-8 py-8">
        <button ref={closeRef} onClick={onClose} className="font-display uppercase tracking-wide2 text-xs text-[var(--gold-lite)]" data-testid="entity-drawer-close">← Close</button>
        {!detail ? (
          <div className="text-[var(--muted)] py-20 text-center">Loading…</div>
        ) : (
          <div className="mt-6">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <h2 id="entity-drawer-title" className="font-serif2 text-2xl text-[var(--bone)]">{detail.entity_name}</h2>
              <StatusPill status={detail.close_status} size="lg" />
            </div>
            <p className="mt-2 text-sm text-[var(--muted)]">{detail.period} · {detail.period_state}{detail.last_updated ? ` · updated ${fmtDate(detail.last_updated)}` : ""}</p>

            <div className="mt-6 grid grid-cols-2 gap-3">
              <Metric label="Unresolved" value={detail.unresolved_count} testid="drawer-unresolved" tone={detail.unresolved_count ? "attention" : "bone"} />
              <Metric label="Evidence Needed" value={detail.evidence_needed_count} testid="drawer-evidence" />
              <Metric label="Reconciled" value={detail.reconciliation.reconciled} testid="drawer-reconciled" />
              <Metric label="Unreconciled" value={detail.reconciliation.unreconciled_bank_facts} testid="drawer-unreconciled" tone={detail.reconciliation.unreconciled_bank_facts ? "attention" : "bone"} />
            </div>

            <GatesChecklist gates={detail.gates} />

            <PeriodSelector entityId={detail.entity_id} />

            <WhatChanged entityId={detail.entity_id} />

            <Block title="Reconciliation (authoritative: recon)">
              <Row k="Outstanding payments" v={detail.reconciliation.outstanding_payments} />
              <Row k="Outstanding receipts" v={detail.reconciliation.outstanding_receipts} />
              <Row k="Exceptions" v={Object.entries(detail.reconciliation.exceptions).filter(([, n]) => n).map(([k, n]) => `${k}:${n}`).join(", ") || "none"} />
            </Block>

            <Block title="Cash reconciliation gate (adapter → close_control)">
              <Row k="Status" v={detail.cash_gate.cash_reconciliation_status} />
              <Row k="Blocks close" v={detail.cash_gate.blocks_close ? "YES" : "no"} />
              <Row k="Matched / unmatched" v={`${detail.cash_gate.matched_count ?? "—"} / ${detail.cash_gate.unmatched_count ?? "—"}`} />
              <Row k="Blocked (exceptions)" v={detail.cash_gate.blocked_count ?? "—"} />
              <Row k="Awaiting evidence" v={detail.cash_gate.awaiting_evidence_count ?? "—"} />
              <Row k="Last reconciled" v={fmtDate(detail.cash_gate.last_reconciled_at)} />
              <Row k="Period scoped" v={detail.cash_gate.period_scoped ? "by transaction_date" : "entity-wide (no period bounds)"} />
              <Row k="Source" v={detail.cash_gate.source_status} />
            </Block>

            <Block title="Receivables (AR) gate (adapter → close_control)">
              <Row k="Status" v={detail.receivables_gate.receivables_status} />
              <Row k="Blocks close" v={detail.receivables_gate.blocks_close ? "YES" : "no"} />
              <Row k="Invoices (finalized / total)" v={`${detail.receivables_gate.finalized_count ?? "—"} / ${detail.receivables_gate.total_invoices ?? "—"}`} />
              <Row k="Unfinalized in-period" v={detail.receivables_gate.unfinalized_count ?? "—"} />
              <Row k="Disputed / uncollectible" v={detail.receivables_gate.disputed_count ?? "—"} />
              <Row k="Open receivables (carried)" v={detail.receivables_gate.open_receivable_count ?? "—"} />
              <Row k="Period scoped" v={detail.receivables_gate.period_scoped ? "by issue_date" : "entity-wide"} />
              <Row k="Source" v={detail.receivables_gate.source_status} />
            </Block>

            <Block title="Payables (AP) gate (adapter → close_control)">
              <Row k="Status" v={detail.payables_gate.payables_status} />
              <Row k="Blocks close" v={detail.payables_gate.blocks_close ? "YES" : "no"} />
              <Row k="Bills (finalized / total)" v={`${detail.payables_gate.finalized_count ?? "—"} / ${detail.payables_gate.total_bills ?? "—"}`} />
              <Row k="Unfinalized in-period" v={detail.payables_gate.unfinalized_count ?? "—"} />
              <Row k="Disputed" v={detail.payables_gate.disputed_count ?? "—"} />
              <Row k="Open payables (carried)" v={detail.payables_gate.open_payable_count ?? "—"} />
              <Row k="Period scoped" v={detail.payables_gate.period_scoped ? "by bill_date" : "entity-wide"} />
              <Row k="Source" v={detail.payables_gate.source_status} />
            </Block>

            <Block title="Close controls (authoritative: close_control)">
              <Row k="Readiness" v={detail.close_readiness_state} />
              <Row k="Required / completed" v={detail.controls.required_total == null ? "NO ACTIVE CYCLE" : `${detail.controls.completed} / ${detail.controls.required_total}`} />
              <Row k="Unresolved controls" v={detail.controls.unresolved.join(", ") || "none"} />
              <Row k="Evidence missing" v={detail.controls.evidence_missing.join(", ") || "none"} />
              <Row k="Reviews pending" v={detail.controls.reviews_pending.join(", ") || "none"} />
              <Row k="Not-yet-wired" v={detail.controls.blocked_not_yet_wired.join(", ") || "none"} />
            </Block>

            <Block title="Ledger integrity (authoritative: ledger)">
              <Row k="Integrity ok" v={detail.ledger_integrity.ok ? "YES" : "NO — violations present"} />
              <Row k="Trial balance" v={detail.ledger_integrity.balanced ? "BALANCED" : "NOT BALANCED"} />
              <Row k="Posted lines" v={detail.ledger_integrity.posted_line_count} />
            </Block>

            {detail.attention?.length > 0 && (
              <Block title="Founder attention">
                {detail.attention.map((a, i) => (
                  <div key={i} className="py-2 border-t border-[rgba(180,139,63,0.12)] first:border-t-0">
                    <p className="font-display uppercase tracking-wide2 text-[10px] text-[#e7a877]">{a.reason_code}</p>
                    <p className="mt-1 text-sm text-[var(--muted)]">{a.why}</p>
                  </div>
                ))}
              </Block>
            )}

            <Block title="Periods (authoritative: ledger)">
              {detail.periods?.length ? detail.periods.map((per) => (
                <Row key={per.period_id} k={`FY${per.fiscal_year} · P${per.period_no}`} v={per.state} />
              )) : <p className="text-sm text-[var(--muted)]">AWAITING_SOURCE — no fiscal periods opened.</p>}
            </Block>

            <p className="mt-6 text-[10px] uppercase tracking-wide2 text-[var(--muted)]">
              Sources: {detail.provenance?.sources?.join(" · ")} · generated {fmtDate(detail.provenance?.generated_at)}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

const transitionTone = (after) => ({ SATISFIED: "text-[#8fcbac]", BLOCKED: "text-[#e88f6a]", NEEDS_ATTENTION: "text-[#e7a877]", NOT_STARTED: "text-[var(--muted)]", UNKNOWN: "text-[#e7a877]" }[after] || "text-[var(--bone)]");

function WhatChanged({ entityId }) {
  const [data, setData] = useState(null);
  const [priorId, setPriorId] = useState("");
  const [err, setErr] = useState(null);
  const load = useCallback((pid) => {
    setErr(null); setData(null);
    fetchCloseWhatChanged(entityId, pid || undefined)
      .then(setData)
      .catch((e) => setErr(formatApiError(e.response?.data?.detail, "What Changed is unavailable.")));
  }, [entityId]);
  useEffect(() => {
    setPriorId("");
    load("");
  }, [load]);

  return (
    <div className="mt-6" data-testid="what-changed">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
        <p className="font-display uppercase tracking-wide2 text-[11px] text-[var(--gold)]/80">What Changed</p>
        {data?.available_snapshots?.length > 0 && (
          <select
            className="bg-transparent border border-[rgba(180,139,63,0.3)] text-xs text-[var(--bone)] px-2 py-1"
            data-testid="what-changed-prior-select" value={priorId}
            onChange={(e) => { setPriorId(e.target.value); load(e.target.value); }}>
            <option value="">Auto (latest earlier snapshot)</option>
            {data.available_snapshots.map((s) => (
              <option key={s.snapshot_id} value={s.snapshot_id}>{s.kind} · {fmtDate(s.as_of)}</option>
            ))}
          </select>
        )}
      </div>
      <div className="border border-[rgba(180,139,63,0.18)] px-4 py-3">
        {err ? (
          <p className="text-sm text-[#e88f6a]" data-testid="what-changed-error">{err}</p>
        ) : !data ? (
          <p className="text-sm text-[var(--muted)]" data-testid="what-changed-loading">Loading changes…</p>
        ) : data.status === "NO_EARLIER_SNAPSHOT" ? (
          <p className="text-sm text-[var(--muted)]" data-testid="what-changed-none">No earlier snapshot available</p>
        ) : data.no_changes ? (
          <p className="text-sm text-[var(--muted)]" data-testid="what-changed-nochanges">No meaningful changes since the selected snapshot.</p>
        ) : (
          <div data-testid="what-changed-diff">
            <p className="text-[11px] text-[var(--muted)] mb-3" data-testid="what-changed-asof">
              {data.prior.kind} ({fmtDate(data.prior.as_of)}) → {data.against.label} ({fmtDate(data.against.as_of)})
              {data.before_from_legacy_snapshot ? " · prior gate states unknown" : ""}
            </p>
            {data.period_change && (
              <p className="text-sm text-[#e7a877] mb-2" data-testid="wc-period">Period: {String(data.period_change.before)} → {String(data.period_change.after)}</p>
            )}
            {data.status_change && (
              <p className="text-sm mb-2" data-testid="wc-status">Close status: <span className="text-[var(--muted)]">{data.status_change.before}</span> → <span className="text-[var(--bone)]">{data.status_change.after}</span></p>
            )}
            {data.readiness && (
              <p className={`text-sm mb-2 ${data.readiness.gained ? "text-[#8fcbac]" : data.readiness.lost ? "text-[#e88f6a]" : "text-[var(--bone)]"}`} data-testid="wc-readiness">
                Readiness {data.readiness.gained ? "gained" : data.readiness.lost ? "lost" : "changed"}: {data.readiness.before} → {data.readiness.after}
              </p>
            )}
            {data.integrity && (
              <p className={`text-sm mb-2 ${data.integrity.regressed ? "text-[#e88f6a]" : "text-[#8fcbac]"}`} data-testid="wc-integrity">
                Integrity: {String(data.integrity.before)} → {String(data.integrity.after)}{data.integrity.regressed ? " (regressed)" : ""}
              </p>
            )}
            {data.gate_changes.length > 0 && (
              <div className="mt-2" data-testid="wc-gate-changes">
                {data.gate_changes.map((g) => (
                  <div key={g.gate} className="flex items-center justify-between gap-3 py-1.5 border-t border-[rgba(180,139,63,0.1)] first:border-t-0" data-testid={`wc-gate-${g.gate.toLowerCase()}`}>
                    <span className="text-sm text-[var(--bone)]">
                      {g.gate}
                      {data.newly_blocked.some((x) => x.gate === g.gate) && <span className="ml-2 text-[10px] uppercase tracking-wide2 text-[#e88f6a]">newly blocked</span>}
                      {data.newly_satisfied.some((x) => x.gate === g.gate) && <span className="ml-2 text-[10px] uppercase tracking-wide2 text-[#8fcbac]">newly satisfied</span>}
                    </span>
                    <span className="text-xs">
                      <span className="text-[var(--muted)]">{g.before}</span>
                      <span className="text-[var(--muted)]"> → </span>
                      <span className={transitionTone(g.after)}>{g.after}</span>
                      {g.source ? <span className="text-[var(--muted)]"> · {g.source}</span> : ""}
                    </span>
                  </div>
                ))}
              </div>
            )}
            {(data.exceptions_opened.length > 0 || data.exceptions_resolved.length > 0) && (
              <div className="mt-3 pt-2 border-t border-[rgba(180,139,63,0.12)]" data-testid="wc-exceptions">
                {data.exceptions_opened.map((c) => (
                  <p key={`o-${c}`} className="text-xs text-[#e88f6a]" data-testid={`wc-exc-open-${c}`}>Exception opened: {c}</p>
                ))}
                {data.exceptions_resolved.map((c) => (
                  <p key={`r-${c}`} className="text-xs text-[#8fcbac]" data-testid={`wc-exc-resolved-${c}`}>Exception resolved: {c}</p>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const periodTone = (s) => ({ HARD_CLOSED: "text-[#8fcbac] border-[#6fae8f]", REOPENED: "text-[#e7a877] border-[#d98b5f]", SUPERSEDED: "text-[var(--muted)] border-[rgba(180,139,63,0.3)]", OPEN: "text-[var(--gold-lite)] border-[rgba(180,139,63,0.5)]", NO_ACTIVITY: "text-[var(--muted)] border-[rgba(180,139,63,0.2)]" }[s] || "text-[var(--muted)] border-[rgba(180,139,63,0.2)]");
const gTone = (s) => ({ SATISFIED: "text-[#8fcbac]", BLOCKED: "text-[#e88f6a]", NEEDS_ATTENTION: "text-[#e7a877]", NOT_STARTED: "text-[var(--muted)]", UNKNOWN: "text-[#e7a877]" }[s] || "text-[var(--bone)]");

function PeriodSelector({ entityId }) {
  const [data, setData] = useState(null);
  const [pid, setPid] = useState("");
  const [err, setErr] = useState(null);
  const load = useCallback((id) => {
    setErr(null); setData(null);
    fetchClosePeriodView(entityId, id || undefined).then((d) => { setData(d); setPid(d.selected_period?.period_id || ""); })
      .catch((e) => setErr(formatApiError(e.response?.data?.detail, "Period view unavailable.")));
  }, [entityId]);
  useEffect(() => { load(""); }, [load]);

  return (
    <div className="mt-6" data-testid="period-selector">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
        <p className="font-display uppercase tracking-wide2 text-[11px] text-[var(--gold)]/80">Historical Period</p>
        {data?.available_periods?.length > 0 && (
          <select className="bg-transparent border border-[rgba(180,139,63,0.3)] text-xs text-[var(--bone)] px-2 py-1"
            data-testid="period-select" value={pid} onChange={(e) => { setPid(e.target.value); load(e.target.value); }}>
            {data.available_periods.map((p) => (
              <option key={p.period_id} value={p.period_id}>{p.label}{p.is_current ? " (current)" : ""} · {p.state}</option>
            ))}
          </select>
        )}
      </div>
      <div className="border border-[rgba(180,139,63,0.18)] px-4 py-3">
        {err ? <p className="text-sm text-[#e88f6a]" data-testid="period-error">{err}</p>
        : !data ? <p className="text-sm text-[var(--muted)]" data-testid="period-loading">Loading period…</p>
        : data.status === "NO_PERIODS" ? <p className="text-sm text-[var(--muted)]" data-testid="period-no-periods">No periods available</p>
        : (
          <div data-testid="period-view">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className="text-sm text-[var(--bone)]">{data.selected_period.label}</span>
              <span className={`text-[10px] uppercase tracking-wide2 px-2 py-0.5 border ${periodTone(data.selected_period.status_label)}`} data-testid="period-status">{data.selected_period.status_label}</span>
              {data.selected_period.is_current && <span className="text-[10px] uppercase tracking-wide2 text-[var(--gold-lite)]">current</span>}
            </div>
            <p className="text-[11px] text-[var(--muted)] mb-3" data-testid="period-source">
              {data.view_source === "LIVE" ? "Live projection (current period)" : data.view_source === "SNAPSHOT" ? `Immutable snapshot · as of ${fmtDate(data.gates_as_of)}` : data.view_source === "NO_SNAPSHOT" ? "No snapshot captured for this period" : "No close activity for this period"}
            </p>

            {data.gates ? (
              <div data-testid="period-gates">
                {data.gates.map((g) => (
                  <div key={g.gate} className="flex items-center justify-between py-1.5 border-t border-[rgba(180,139,63,0.1)] first:border-t-0" data-testid={`period-gate-${g.gate.toLowerCase()}`}>
                    <span className="text-sm text-[var(--bone)]">{g.gate}</span>
                    <span className={`text-xs ${gTone(g.state)}`}>{g.state}{g.source ? <span className="text-[var(--muted)]"> · {g.source}</span> : ""}</span>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-[var(--muted)]" data-testid="period-gates-none">{data.view_source === "NO_SNAPSHOT" ? "No snapshot — gate states unavailable (not zero)." : "No activity — nothing to display."}</p>}

            {data.snapshots.length > 0 && (
              <div className="mt-3 pt-2 border-t border-[rgba(180,139,63,0.12)]" data-testid="period-snapshots">
                <p className="text-[10px] uppercase tracking-wide2 text-[var(--gold)]/70 mb-1">Immutable snapshots</p>
                {data.snapshots.map((s) => (
                  <p key={s.snapshot_id} className="text-xs text-[var(--muted)]" data-testid={`period-snap-${s.snapshot_id}`}>{s.kind} · iter {s.iteration ?? "—"} · {fmtDate(s.as_of)}</p>
                ))}
              </div>
            )}

            {data.what_changed_history.length > 0 && (
              <div className="mt-3 pt-2 border-t border-[rgba(180,139,63,0.12)]" data-testid="period-history">
                <p className="text-[10px] uppercase tracking-wide2 text-[var(--gold)]/70 mb-1">What changed (within this period)</p>
                {data.what_changed_history.map((h, i) => (
                  <p key={i} className="text-xs text-[var(--muted)]">{h.from.kind} → {h.to.kind}: {h.newly_blocked.length} blocked, {h.newly_satisfied.length} satisfied{h.exceptions_opened.length ? `, +${h.exceptions_opened.length} exc` : ""}{h.exceptions_resolved.length ? `, −${h.exceptions_resolved.length} exc` : ""}</p>
                ))}
              </div>
            )}

            {data.exceptions.length > 0 && (
              <div className="mt-3 pt-2 border-t border-[rgba(180,139,63,0.12)]" data-testid="period-exceptions">
                <p className="text-[10px] uppercase tracking-wide2 text-[var(--gold)]/70 mb-1">Exceptions</p>
                {data.exceptions.map((e) => (
                  <p key={e.exc_id} className="text-xs text-[var(--muted)]">{e.reason_code} · {e.severity} · {e.disposition}</p>
                ))}
              </div>
            )}

            {data.approvals.some((a) => a.approved_by || a.hard_closed_by) && (
              <div className="mt-3 pt-2 border-t border-[rgba(180,139,63,0.12)]" data-testid="period-approvals">
                <p className="text-[10px] uppercase tracking-wide2 text-[var(--gold)]/70 mb-1">Approvals</p>
                {data.approvals.map((a) => (
                  <p key={a.cycle_id} className="text-xs text-[var(--muted)]">iter {a.iteration ?? "—"} · {a.state}{a.approved_by ? ` · approved ${fmtDate(a.approved_at)}` : ""}{a.hard_closed_by ? ` · hard-closed ${fmtDate(a.hard_closed_at)}` : ""}{a.superseded ? " · superseded" : ""}</p>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const Block = ({ title, children }) => (
  <div className="mt-6">
    <p className="font-display uppercase tracking-wide2 text-[11px] text-[var(--gold)]/80 mb-2">{title}</p>
    <div className="border border-[rgba(180,139,63,0.18)] px-4 py-2">{children}</div>
  </div>
);
const Row = ({ k, v }) => (
  <div className="flex items-start justify-between gap-4 py-2 border-t border-[rgba(180,139,63,0.1)] first:border-t-0">
    <span className="text-sm text-[var(--muted)]">{k}</span>
    <span className="text-sm text-[var(--bone)] text-right">{String(v)}</span>
  </div>
);

function PortfolioOverview({ onOpenEntity }) {
  const [pf, setPf] = useState(null);
  const [err, setErr] = useState(null);
  const load = () => { setErr(null); setPf(null); fetchClosePortfolio().then(setPf).catch((e) => setErr(formatApiError(e.response?.data?.detail, "Portfolio oversight is unavailable."))); };
  useEffect(() => { load(); }, []);
  if (err) return (
    <section className="mt-10 corner-frame px-6 py-6" data-testid="portfolio-error" aria-labelledby="portfolio-h">
      <h2 id="portfolio-h" className="font-display uppercase tracking-wide2 text-[12px] text-[#e7a877]">Portfolio Close Oversight — unavailable</h2>
      <p className="mt-2 text-sm text-[var(--muted)]">{err}</p>
      <button onClick={load} className="jmh-cta mt-4" data-testid="portfolio-retry">Try again</button>
    </section>
  );
  if (!pf) return <div className="mt-8 text-[var(--muted)] text-sm" data-testid="portfolio-loading">Loading portfolio…</div>;
  const a = pf.aggregates;
  // Derive gate columns from the live canonical registry (aggregates.blocked_by is keyed by _CANON_GATES in
  // order) so the matrix can NEVER drift from the backend gate set again. Do not hardcode a gate count.
  const gateNames = Object.keys(a.blocked_by || {});
  return (
    <section className="mt-10" data-testid="portfolio-overview" aria-labelledby="portfolio-h">
      <h2 id="portfolio-h" className="font-display uppercase tracking-wide2 text-[12px] text-[var(--gold)]/80">Portfolio Close Oversight <span className="text-[var(--muted)] normal-case tracking-normal">· uneliminated, not consolidated</span></h2>
      <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="portfolio-summary">
        <Metric label="Ready" value={a.ready} testid="pf-ready" />
        <Metric label="Blocked" value={a.blocked} testid="pf-blocked" tone={a.blocked ? "attention" : "bone"} />
        <Metric label="Not started" value={a.not_started} testid="pf-notstarted" />
        <Metric label="Unknown" value={a.unknown} testid="pf-unknown" tone={a.unknown ? "attention" : "bone"} />
      </div>
      <div className="mt-3 flex flex-wrap gap-2" data-testid="portfolio-blocker-rollup">
        {gateNames.map((g) => (
          <span key={g} className="border border-[rgba(180,139,63,0.25)] px-3 py-1.5 text-[11px] uppercase tracking-wide2 text-[var(--bone)]"
            data-testid={`rollup-${g.toLowerCase()}`}>{g}: <span className={a.blocked_by[g] ? "text-[#e88f6a]" : "text-[var(--muted)]"}>{a.blocked_by[g]}</span> blocked</span>
        ))}
      </div>
      <p className="mt-2 text-[11px] text-[var(--muted)]">A single entity may have several unmet requirements. The counts overlap and should not be added together to determine the number of affected entities.</p>
      <div className="mt-4 overflow-x-auto border border-[rgba(180,139,63,0.18)]">
        <table className="w-full text-left border-collapse min-w-[640px]" data-testid="portfolio-matrix">
          <caption className="sr-only">Per-entity close gate matrix</caption>
          <thead>
            <tr className="text-[11px] uppercase tracking-wide2 text-[var(--muted)]">
              <th scope="col" className="px-3 py-2">Entity</th>
              {gateNames.map((g) => <th key={g} scope="col" className="px-2 py-2 text-center">{g}</th>)}
              <th scope="col" className="px-3 py-2">Overall</th>
            </tr>
          </thead>
          <tbody>
            {pf.entities.length === 0 && (
              <tr data-testid="pf-empty"><td colSpan={gateNames.length + 2} className="px-3 py-6 text-center text-sm text-[var(--muted)]">No entities in scope.</td></tr>
            )}
            {pf.entities.map((e) => (
              <tr key={e.entity_id} className="border-t border-[rgba(180,139,63,0.1)]" data-testid={`pf-row-${e.entity_id}`}>
                <th scope="row" className="px-3 py-2 font-normal">
                  <button onClick={() => onOpenEntity(e.entity_id)} className="text-[var(--bone)] hover:text-[var(--gold)] text-sm underline-offset-2 hover:underline"
                    data-testid={`pf-open-${e.entity_id}`} aria-label={`Open close detail for ${e.entity_name}`}>{e.entity_name}</button>
                  <span className="block text-[11px] uppercase tracking-wide2 text-[var(--gold-lite)]/60">{e.verification_state}{e.contract_anomaly ? " · gate data unavailable" : ""}</span>
                </th>
                {e.gates.map((g) => {
                  const m = GATE_META[g.state] || GATE_META.UNKNOWN;
                  return <td key={g.gate} className="px-2 py-2 text-center" title={`${g.gate}: ${m.label}`}>
                    <span role="img" className={`${m.tone} text-[13px]`} aria-label={`${g.gate} ${m.label}`}>{m.glyph}</span></td>;
                })}
                <td className="px-3 py-2"><span className={`text-[11px] uppercase tracking-wide2 ${(GATE_META[e.overall === "READY_TO_CLOSE" ? "SATISFIED" : e.overall === "BLOCKED" ? "BLOCKED" : e.overall === "NEEDS_ATTENTION" ? "NEEDS_ATTENTION" : e.overall === "UNKNOWN" ? "UNKNOWN" : "NOT_STARTED"]).tone}`}>{e.overall}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Console({ onSignOut }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [drawer, setDrawer] = useState(null);

  const load = async () => {
    setLoading(true); setErr(null);
    try { setData(await fetchCloseStatus()); }
    catch (e) { setErr(formatApiError(e.response?.data?.detail, "Could not load close status.")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const sc = data?.safe_counts;

  return (
    <div className="min-h-screen glow-navy px-5 md:px-10 py-10">
      <Seo title="JMH — Founder Close Console" />
      <div className="max-w-7xl 2xl:max-w-[1680px] mx-auto">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="font-display uppercase tracking-luxe text-[10px] text-[var(--gold)]/80">Jennae Michelle Holdings · CFO</p>
            <h1 className="mt-1 font-serif2 text-4xl md:text-5xl text-[var(--bone)]">Founder Close Console</h1>
            <p className="mt-2 text-sm text-[var(--muted)]">Read-only period-close readiness across your entities.</p>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={load} className="font-display uppercase tracking-wide2 text-xs text-[var(--gold-lite)]" data-testid="close-refresh">Refresh</button>
            <button onClick={onSignOut} className="font-display uppercase tracking-wide2 text-xs text-[var(--gold-lite)]" data-testid="close-signout">Sign out</button>
          </div>
        </div>

        {loading ? (
          <div className="text-[var(--muted)] py-24 text-center" data-testid="close-loading">Loading close status…</div>
        ) : err ? (
          <div className="corner-frame mt-10 px-6 py-10 text-center" data-testid="close-error">
            <p className="text-[#e7a877] font-serif2 text-xl">Unable to load</p>
            <p className="mt-2 text-sm text-[var(--muted)]">{err}</p>
            <button onClick={load} className="jmh-cta mt-6" data-testid="close-retry">Try again</button>
          </div>
        ) : (
          <>
            {/* overall banner */}
            <div className="mt-8 corner-frame px-6 py-6 flex items-center justify-between gap-4 flex-wrap" data-testid="close-overall">
              <div>
                <p className="font-display uppercase tracking-wide2 text-[10px] text-[var(--muted)]">Overall — {data.period_scope}</p>
                <p className="mt-2 font-serif2 text-2xl text-[var(--bone)]">Are we ready to close?</p>
                <p className="mt-1 text-xs text-[var(--muted)]">Generated {fmtDate(data.generated_at)} · env {data.environment}</p>
              </div>
              <StatusPill status={data.overall_status} size="lg" />
            </div>

            {/* summary metrics — desktop multi-column */}
            <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="close-metrics">
              <Metric label="Entities" value={sc.entities_in_scope} testid="m-entities" />
              <Metric label="Needs Attention" value={sc.entities_needing_attention} testid="m-attention" tone={sc.entities_needing_attention ? "attention" : "bone"} />
              <Metric label="Ready" value={sc.entities_ready} testid="m-ready" />
              <Metric label="Closed" value={sc.entities_closed} testid="m-closed" />
              <Metric label="Unreconciled" value={sc.total_unreconciled_bank_facts} testid="m-unreconciled" tone={sc.total_unreconciled_bank_facts ? "attention" : "bone"} />
              <Metric label="Evidence Needed" value={sc.total_evidence_needed} testid="m-evidence" />
            </div>

            {/* exception-first: attention before the roster */}
            <div className="mt-8">
              <AttentionSection items={data.attention_items} />
            </div>

            <PortfolioOverview onOpenEntity={(id) => setDrawer(id)} />

            {/* entity roster */}
            <div className="mt-8">
              <GateStateLegend gates={[]} />
              <p className="font-display uppercase tracking-wide2 text-[11px] text-[var(--gold)]/80 mb-3">Entity Close Status</p>
              {data.entity_summary.length === 0 ? (
                <div className="corner-frame px-6 py-12 text-center" data-testid="close-empty">
                  <p className="text-[var(--bone)] font-serif2 text-xl">No entities in scope</p>
                  <p className="mt-2 text-sm text-[var(--muted)]">Register a legal entity in the ledger to begin period-close tracking.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-3" data-testid="entity-grid">
                  {data.entity_summary.map((e) => (
                    <article key={e.entity_id}
                      className="border border-[rgba(180,139,63,0.2)] hover:border-[var(--gold)] transition-colors px-5 py-4"
                      data-testid={`entity-card-${e.entity_id}`} aria-label={`${e.entity_name} close status ${e.close_status}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-[var(--bone)] font-serif2 text-xl truncate">{e.entity_name}</p>
                          <p className="text-xs text-[var(--muted)] mt-0.5">{e.period} · {e.period_state}</p>
                          <p className="text-[10px] font-mono text-[var(--gold-lite)]/55 mt-0.5" data-testid={`entity-code-${e.entity_id}`}>#{e.entity_id.slice(0, 8)}</p>
                        </div>
                        <StatusPill status={e.close_status} />
                      </div>
                      <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                        <div>
                          <p className="font-serif2 text-lg text-[var(--bone)]">{e.unresolved_count}</p>
                          <p className="text-[10px] uppercase tracking-wide2 text-[var(--gold-lite)]/75">Unresolved</p>
                        </div>
                        <div>
                          <p className="font-serif2 text-lg text-[var(--bone)]">{e.reconciliation.unreconciled_bank_facts}</p>
                          <p className="text-[10px] uppercase tracking-wide2 text-[var(--gold-lite)]/75">Unrecon.</p>
                        </div>
                        <div>
                          <p className="font-serif2 text-lg text-[var(--bone)]">{e.evidence_needed_count}</p>
                          <p className="text-[10px] uppercase tracking-wide2 text-[var(--gold-lite)]/75">Evidence</p>
                        </div>
                      </div>
                      <GateStrip gates={e.gates} entityId={e.entity_id} />
                      {e.attention_required && (
                        <p className="mt-3 font-display uppercase tracking-wide2 text-[9px] text-[#e7a877]">● Founder attention required</p>
                      )}
                      <button onClick={() => setDrawer(e.entity_id)}
                        className="jmh-cta mt-4 w-full text-center" data-testid={`entity-open-${e.entity_id}`}
                        aria-label={`View close detail for ${e.entity_name}`}>
                        View detail
                      </button>
                    </article>
                  ))}
                </div>
              )}
            </div>

            <p className="mt-10 text-[10px] uppercase tracking-wide2 text-[var(--muted)]" data-testid="close-provenance">
              Read-only projection · sources: {data.provenance?.authoritative_sources?.join(" · ")} · no money moved
            </p>
          </>
        )}
      </div>
      {drawer && <EntityDrawer entityId={drawer} onClose={() => setDrawer(null)} />}
    </div>
  );
}

export default function FounderCloseConsole() {
  const [authed, setAuthed] = useState(null);
  useEffect(() => {
    if (!getToken()) { setAuthed(false); return; }
    adminMe().then(() => setAuthed(true)).catch(() => { clearToken(); setAuthed(false); });
  }, []);
  if (authed === null) return <div className="min-h-screen glow-navy" />;
  if (!authed) return <LoginView onAuthed={() => setAuthed(true)} />;
  return <Console onSignOut={() => { clearToken(); setAuthed(false); }} />;
}
